import type { AlertProject } from '@kirin/api-client/contracts/alerts';
import {
  PLATFORM_REGISTRY,
  type PlatformId,
  type PlatformMetadata,
} from '@kirin/api-client/contracts/platform-registry';
import {
  CONFIDENCE_HIGH_MIN,
  CONFIDENCE_MEDIUM_MIN,
  getEntropyConfidence,
  type SecretConfidence,
} from '@kirin/api-client/helpers/secret-confidence';

// Re-exported from the shared api-client helper (single source of truth for the
// entropy bands). Kept re-exported here so existing frontend imports from
// `./AlertSecretDetails.helpers` keep working unchanged.
export {
  CONFIDENCE_HIGH_MIN,
  CONFIDENCE_MEDIUM_MIN,
  getEntropyConfidence,
  type SecretConfidence,
};

/**
 * KIR-3765 secret-detection enrichment surfaced on a secret alert. All fields
 * are optional and the panel renders only when at least one is present.
 */
export interface AlertSecretDetailsData {
  engine?: string;
  ruleId?: string;
  /**
   * Readable rule name for Kirin (regex/file-pattern) rules, e.g.
   * "AWS Access Key ID". Absent for gitleaks (its `ruleId` is already readable)
   * and for older agents/extensions that predate this field.
   */
  ruleName?: string;
  entropy?: number;
  toolName?: string;
  sessionId?: string;
  workspaceName?: string;
  /**
   * Short, doubly-redacted excerpt of the scanned content around the secret
   * (~50 chars either side), with the secret and any surrounding PII masked by
   * the backend at ingest (KIR-3765). Absent for older alerts and when the
   * backend couldn't build a screened snippet. A best-effort, representative
   * excerpt — never raw content.
   */
  matchContext?: string;
  project?: AlertProject;
  /**
   * Client/platform that produced the alert, narrowed to a canonical
   * {@link PlatformId} from the platform registry (via `toPlatformId`). Derived
   * from the alert's `clientTypes` and used to point the Session ID hint at the
   * right on-disk transcript location. Undefined when the platform is unknown,
   * unregistered, or ambiguous (a grouped alert spanning multiple clients).
   */
  platform?: PlatformId;
}

/**
 * Label for the "Rule" row, annotated with the rule's source engine:
 * "Rule (Gitleaks)" for gitleaks, "Rule (Kirin)" for Kirin's own regex /
 * file-pattern rules, and a bare "Rule" when the engine is unknown (older
 * agents/extensions that predate enrichment).
 */
export function ruleRowLabel(engine: string | undefined): string {
  if (engine === 'gitleaks') return 'Rule (Gitleaks)';
  if (engine === 'regex' || engine === 'file-pattern') return 'Rule (Kirin)';
  return 'Rule';
}

/**
 * True when the rule name should be shown alongside the raw rule (`ruleId`).
 * Only Kirin engines (regex/file-pattern) carry a `ruleName` worth surfacing —
 * gitleaks' `ruleId` is already the readable name.
 */
export function shouldShowRuleName(
  engine: string | undefined,
  ruleName: string | undefined
): boolean {
  return Boolean(ruleName) && engine !== 'gitleaks';
}

/**
 * Plain-text explanation of the confidence scale, used for the entropy row's
 * accessible hint label and the confidence badge's `aria-label`. The visible
 * tooltip body is rendered by `ConfidenceScaleExplanation` (richer markup).
 */
export const CONFIDENCE_SCALE_HINT =
  'Confidence that this is a real secret, derived from the matched value’s randomness (Shannon entropy) reported by the Gitleaks engine. Bands follow Gitleaks’ default rule thresholds, which top out at 4.5: Low (entropy below 3) is below most Gitleaks thresholds — often a placeholder or low-randomness token; Medium (3 to below 4.5) is within Gitleaks’ stricter thresholds — likely a real secret; High (4.5 and above) meets or exceeds every default threshold — very likely a randomly generated credential.';

/**
 * Base explanation for the Session ID row, independent of platform. The
 * platform-specific on-disk location (see {@link sessionTranscriptLocation}) is
 * appended when known, via {@link SESSION_ID_TRANSCRIPT_HINT}.
 *
 * The determiner is deliberately hedged. `sessionId` is a representative value,
 * not a group aggregate: it is not part of `finding_key`, so one grouped alert
 * routinely spans several sessions and only the most recent is shown. Saying
 * "the session" would assert a single session for the whole group.
 */
export const SESSION_ID_HINT =
  'The assistant session/conversation that ran the tool where the secret surfaced, for this alert’s most recent occurrence; other occurrences in this group may come from different sessions.';

/**
 * Transcript follow-up for the Session ID row. Emitted only when
 * {@link sessionTranscriptLocation} returns a path — promising a transcript
 * unconditionally dangles for platforms whose on-disk location we don't
 * document, which is why this is separate from {@link SESSION_ID_HINT}.
 */
export const SESSION_ID_TRANSCRIPT_HINT =
  'Use it to find that session’s transcript on the developer’s machine.';

/**
 * Platform-agnostic follow-up tip for the Session ID row: coding agents can
 * look up their own past sessions by id, so the quickest way to see what
 * happened is to ask the agent to open/summarize that session. Shown for every
 * platform, since it works even where we don't document an on-disk path.
 */
export const SESSION_ID_AGENT_HINT =
  'Tip: you can ask the coding agent to open or summarize this session by its ID.';

/**
 * On-disk location of a session's transcript for the alert's platform, read
 * from the platform registry's `sessionTranscriptLocation` template, or `null`
 * when the platform documents none (the hint then shows the generic explanation
 * only, so we never point the user at a made-up path).
 *
 * The registry field is required per platform, so adding a new platform forces
 * an explicit decision there — this helper just interpolates the concrete
 * `sessionId` into the template.
 *
 * @param platform - the alert's `platform` (narrowed from `clientTypes`).
 * @param sessionId - substituted for the `{sessionId}` token in the template.
 */
export function sessionTranscriptLocation(
  platform: PlatformId | undefined,
  sessionId: string
): string | null {
  if (!platform) {
    return null;
  }
  return interpolateSessionId(
    PLATFORM_REGISTRY[platform].sessionTranscriptLocation,
    sessionId
  );
}

/**
 * Secondary on-disk location for the alert's platform, or `null` when it
 * documents only one. Rendered as an extra line under the main Location so a
 * reader whose session took the other branch isn't sent to a missing file — see
 * `sessionTranscriptLocationAlt` in the registry for when this is warranted.
 *
 * No need to check the primary location here: the registry type makes an
 * alternate without a primary unrepresentable, so a non-null return from this
 * helper implies {@link sessionTranscriptLocation} is non-null too, and the
 * extra line never renders without a Location above it.
 *
 * @param platform - the alert's `platform` (narrowed from `clientTypes`).
 * @param sessionId - substituted for the `{sessionId}` token in the template.
 */
export function sessionTranscriptLocationAlt(
  platform: PlatformId | undefined,
  sessionId: string
): { when: string; path: string } | null {
  if (!platform) {
    return null;
  }
  // Read through PlatformMetadata, not off PLATFORM_REGISTRY directly: the
  // registry is `as const`, so an entry's inferred type lists only the keys it
  // literally declares, and the optional field is absent from every platform
  // that omits it. Same widening the backend does in classify-ai-instruction.
  const meta: PlatformMetadata = PLATFORM_REGISTRY[platform];
  const alt = meta.sessionTranscriptLocationAlt;
  const path = interpolateSessionId(alt?.template, sessionId);
  return alt && path ? { when: alt.when, path } : null;
}

/**
 * Substitutes the concrete `sessionId` into a registry location template,
 * returning `null` for platforms that document no such path.
 *
 * replaceAll, not replace: Cursor's template names the id twice (the
 * agent-transcripts directory and the .jsonl inside it), and `replace` with a
 * string pattern substitutes only the first match — which would render a path
 * ending in a literal `{sessionId}.jsonl`.
 *
 * Use a replacement function, not a string, so a sessionId containing `$&`,
 * `$$`, `$'`, `` $` `` etc. is inserted literally instead of being interpreted
 * as a String.prototype.replace special replacement pattern.
 */
function interpolateSessionId(
  template: string | undefined,
  sessionId: string
): string | null {
  return template?.replaceAll('{sessionId}', () => sessionId) ?? null;
}

/**
 * Returns true when at least one enrichment field is present, so callers can
 * skip rendering the panel (and its header) for alerts with no enrichment.
 */
export function hasSecretDetails(details: AlertSecretDetailsData): boolean {
  return (
    Boolean(details.engine) ||
    Boolean(details.ruleId) ||
    Boolean(details.ruleName) ||
    typeof details.entropy === 'number' ||
    Boolean(details.toolName) ||
    Boolean(details.sessionId) ||
    Boolean(details.project) ||
    Boolean(details.workspaceName) ||
    Boolean(details.matchContext)
  );
}
